import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildPartnerSettingsPayloadFromForm,
  mapPartnerSettingsFieldErrors,
  mapPartnerSettingsResponseToDto,
  validatePartnerSettingsPayload,
} from '../dto/partner-settings.dto.js';
import { buildEmptyPartnerSettingsForm } from '../model/partner-settings.model.js';

function formsEqual(left, right) {
  return [
    'minimumWithdrawalAmount',
    'walletCurrency',
    'walletCurrencyLocked',
  ].every((key) => String(left?.[key] || '') === String(right?.[key] || ''));
}

function normalizeCurrencyOptions(payload, selectedCurrency) {
  const source = payload?.currencies ?? payload?.items ?? payload?.data ?? payload;
  const map = new Map();

  if (Array.isArray(source)) {
    source.forEach((item) => {
      const value = String(item?.value || item?.code || item?.currency || item || '').trim().toUpperCase();
      if (/^[A-Z]{3}$/.test(value)) {
        map.set(value, { value, label: value });
      }
    });
  }

  const selected = String(selectedCurrency || '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(selected) && !map.has(selected)) {
    map.set(selected, { value: selected, label: selected });
  }

  return Array.from(map.values()).sort((left, right) => left.value.localeCompare(right.value));
}

export function usePartnerSettings() {
  const { token } = useAuth();

  const [form, setForm] = useState(buildEmptyPartnerSettingsForm());
  const [initialForm, setInitialForm] = useState(buildEmptyPartnerSettingsForm());
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const [response, currenciesResponse] = await Promise.all([
        adminApi.getPartnerSettings(token),
        adminApi.getCurrenciesDictionary(token).catch(() => null),
      ]);
      const nextForm = mapPartnerSettingsResponseToDto(response);
      setCurrencyOptions(normalizeCurrencyOptions(currenciesResponse, nextForm.walletCurrency));
      setForm(nextForm);
      setInitialForm(nextForm);
    } catch (requestError) {
      const empty = buildEmptyPartnerSettingsForm();
      setCurrencyOptions(normalizeCurrencyOptions(null, empty.walletCurrency));
      setForm(empty);
      setInitialForm(empty);
      setError(requestError.message || 'Failed to load partner settings');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetForm = useCallback(() => {
    if (isSaving) {
      return;
    }

    setForm(initialForm);
    setFormErrors({});
    setError('');
    setSuccess('');
  }, [initialForm, isSaving]);

  const saveForm = useCallback(async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const payload = buildPartnerSettingsPayloadFromForm(form);
    const nextErrors = validatePartnerSettingsPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.updatePartnerSettings(token, payload);
      const nextForm = mapPartnerSettingsResponseToDto(response);
      setForm(nextForm);
      setInitialForm(nextForm);
      setSuccess('Partner settings updated.');
    } catch (requestError) {
      setFormErrors(mapPartnerSettingsFieldErrors(requestError));
      setError(requestError.message || 'Failed to update partner settings');
    } finally {
      setIsSaving(false);
    }
  }, [form, token]);

  return {
    form,
    currencyOptions,
    isLoading,
    isSaving,
    error,
    success,
    formErrors,
    hasChanges: !formsEqual(form, initialForm),
    onFormChange,
    resetForm,
    saveForm,
  };
}
